import type { Order } from '@commercelayer/sdk'
import { getAdyenPaymentMethodId, isAdyenPaymentMethod } from './paymentMethods'

type AdyenPaymentSourceSetters = {
  setPaymentMethod: (id: string) => Promise<void>
  setAdyenPaymentSource: () => Promise<unknown>
}

/**
 * Apple Pay and Google Pay mount together and both need the Adyen payment
 * source: the first one to ask creates it, the other waits for the same call
 * instead of creating a second one. Shared by the cart and the express order.
 */
const adyenPaymentSourceInFlight = new Map<string, Promise<void>>()

/** Sets the Adyen payment method and source on the order, once per order. */
export const ensureOrderAdyenPaymentSource = async (
  order: Order | null | undefined,
  { setPaymentMethod, setAdyenPaymentSource }: AdyenPaymentSourceSetters
) => {
  if (!order?.id) return
  if (order.payment_method && isAdyenPaymentMethod(order.payment_method)) {
    return
  }

  const pending = adyenPaymentSourceInFlight.get(order.id)
  if (pending) return pending

  const orderId = order.id
  const run = (async () => {
    const adyenPaymentMethodId = getAdyenPaymentMethodId(order)
    if (!adyenPaymentMethodId) {
      throw new Error('adyenPaymentMethodId not found')
    }
    await setPaymentMethod(adyenPaymentMethodId)
    await setAdyenPaymentSource()
  })().finally(() => adyenPaymentSourceInFlight.delete(orderId))

  adyenPaymentSourceInFlight.set(orderId, run)
  return run
}

/**
 * Resolves once no Adyen payment source is being set on the order: a wallet
 * setting its own payment method (PayPal) waits for it, or the Adyen one
 * would land afterwards and replace it.
 */
export const waitForOrderAdyenPaymentSource = async (
  orderId: string | null | undefined
) => {
  if (!orderId) return
  await adyenPaymentSourceInFlight.get(orderId)?.catch(() => undefined)
}
